import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { URL_BASE } from '../../core/constants';
import { Carrier } from './carrier.model';
import { HttpUtils } from '../../shared/http-utils';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import { CustomEncoder } from '../../shared/custom-encoder';
import { SortDirection } from '../../shared/sort-direction';
import { CarrierSearchCriteria } from './carrier-search-criteria.model';
import { TmsType } from './tms-type.model';
import { map } from 'rxjs/operators';
import { FileService } from '../../shared/services/file.service';
import { RegionFeatureOverrideable } from './region-features.model';
import { CarrierDisplay } from './carrier-display.model';
import { Region } from './region.model';

@Injectable()
export class CarrierService {

  private CARRIERS_URI = 'carriers';

  constructor(
    private http: HttpClient,
    private fileService: FileService,
  ) {
  }

  public getAll(): Observable<Carrier[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}`;
    return this.http.get(url, { headers })
      .pipe(
        map((carriers: any[]) => carriers.map(c => Carrier.fromDto(c))),
        map(carriers => carriers.sort((c1, c2) => c1.name.localeCompare(c2.name)))
      );
  }

  public getAllLight(): Observable<Carrier[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/light`;
    return this.http.get(url, { headers })
      .pipe(
        map((carriers: any[]) => carriers.map(c => Carrier.fromDto(c))),
        map(carriers => carriers.sort((c1, c2) => c1.name.localeCompare(c2.name)))
      );
  }

  public getAllLightByRegions(regionIds: string[], tmsType?: TmsType): Observable<Carrier[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.CARRIERS_URI}/by/regions`;

    let params = new HttpParams();
    regionIds.forEach(regionId => params = params.append('regionIds', regionId));
    if (tmsType) {
      params = params.set('tmsType', tmsType);
    }

    return this.http.get(url, {headers, params})
      .pipe(map((carriers: any[]) => carriers.map(c => Carrier.fromDto(c))));
  }

  public getAllLightByTmsType(tmsType: TmsType): Observable<Carrier[]> {
    const headers = HttpUtils.createHeaders();
    const params  = { tmsType: tmsType.toString() };
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/by/tms-type`;
    return this.http.get(url, { headers, params })
      .pipe(
        map((carriers: any[]) => carriers.map(c => Carrier.fromDto(c))),
        map(carriers => carriers.sort((c1, c2) => c1.name.localeCompare(c2.name)))
      );
  }

  public getAllLocalLoadCarriers(): Observable<Carrier[]> {
    return this.getAll().pipe(map(carriers => carriers.filter(c => c.tmsType === TmsType.LOCAL)));
  }

  public getById(id: string): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/${id}`;
    return this.http.get(url, { headers })
      .pipe(
        map(c => Carrier.fromDto(c))
      );
  }

  public getByScacCodeOrVendorNumber(scacCode: string, vendorNumber: string): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/by/scac-code/or/vendor-number`;
    const params  = { scacCode: scacCode, vendorNumber: vendorNumber };
    return this.http.get(url, { headers, params })
      .pipe(
        map(c => Carrier.fromDto(c))
      );
  }

  public getSelfCarrier(): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/self`;

    return this.http.get(url, { headers })
      .pipe(
        map(h => Carrier.fromDto(h))
      );
  }

  public create(carrier: Carrier): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}`;

    carrier.code = carrier.code.trim();

    return this.http.post(url, Carrier.toDto(carrier), { headers })
      .pipe(
        map(c => Carrier.fromDto(c))
      );
  }

  public update(carrier: Carrier): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/${carrier.id}`;

    carrier.code = carrier.code.trim();

    return this.http.put(url, Carrier.toDto(carrier), { headers })
      .pipe(
        map(h => Carrier.fromDto(h))
      );
  }

  public updateSelfCarrier(carrier: Carrier): Observable<Carrier> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/self`;

    return this.http.put(url, Carrier.toDto(carrier), { headers })
      .pipe(
        map(h => Carrier.fromDto(h))
      );
  }

  public toggleBlocking(carrier: Carrier): Observable<boolean> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/${carrier.id}/blocked/${!carrier.blocked}`;

    return this.http.post<boolean>(url, {}, { headers });
  }

  public exportCarriers() {
    const url = `${URL_BASE}/api/${this.CARRIERS_URI}/export`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    this.http.get(url, { headers, responseType: 'blob', observe: 'response' })
      .subscribe(response => this.fileService.saveToFileSystem(response));
  }

  public searchByRegion(regionId: string): Observable<Carrier[]> {
    return this.search(0, 1000, 'name', SortDirection.ASC, {region_id: regionId}).pipe(
      map(carrierSearchObjectResult => carrierSearchObjectResult.results)
    );
  }

  public search(
    page: number, size: number, sortColumn: string, direction: SortDirection, criteria: CarrierSearchCriteria
  ): Observable<SearchObjectResult<Carrier>> {

    const params = {
      page: String(page),
      size: String(size),
      sort: sortColumn,
      direction,
      ...criteria
    };

    for (const key of Object.keys(params)) {
      if (!params[key]) {
        delete params[key];
      }
    }

    const headers    = HttpUtils.createHeaders();
    const httpParams = new HttpParams({
      encoder: new CustomEncoder(),
      fromObject: params
    });

    const url = `${URL_BASE}/api/${this.CARRIERS_URI}/search`;

    return this.http.get(url, { headers, params: httpParams })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, Carrier.fromDto))
      );
  }

  public getLightByRegion(region: Region): Observable<Carrier[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.CARRIERS_URI}/by/region/${region.id}/light`;

    return this.http.get(url, { headers })
      .pipe(
        map((carriers: any[]) => carriers.map(c => Carrier.fromDto(c))),
        map(carriers => carriers.sort((c1, c2) => c1.name.localeCompare(c2.name)))
      );
  }

  public searchForDisplayCarriers(
    page: number, size: number, sortColumn: string, direction: SortDirection, criteria: CarrierSearchCriteria
  ): Observable<SearchObjectResult<CarrierDisplay>> {

    const params = {
      page: String(page),
      size: String(size),
      sort: sortColumn,
      direction,
      ...criteria
    };

    for (const key of Object.keys(params)) {
      if (!params[key]) {
        delete params[key];
      }
    }

    const headers    = HttpUtils.createHeaders();
    const httpParams = new HttpParams({
      encoder: new CustomEncoder(),
      fromObject: params
    });

    const url = `${URL_BASE}/api/${this.CARRIERS_URI}/display/search`;

    return this.http.get(url, { headers, params: httpParams })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, CarrierDisplay.fromDto))
      );
  }

  public getCarrierCountHavingRegionFeatureOverride(region: string, feature: RegionFeatureOverrideable): Observable<Carrier[]> {
    const url = `${URL_BASE}/api/${this.CARRIERS_URI}/region-feature/${region}/override/count`;
    const headers = HttpUtils.createHeaders();
    const params = { feature };

    return this.http.get(url, {headers, params})
      .pipe(
        map((result: any[]) => result.map(c => Carrier.fromDto(c)))
      );
  }
}
